'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface SignupFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  referralCode: string;
  agreeTerms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const router = useRouter();
  const { signUp } = useAuth();
  const supabase = createClient();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>({
    defaultValues: { referralCode: '' },
  });

  const passwordValue = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      const { user: newUser } = await signUp(data.email, data.password, { fullName: data.fullName });

      // If a referral code was provided, build the 3-level referral chain
      // and generate a one-time registration bonus for the referrer
      if (data.referralCode && data.referralCode.trim() && newUser?.id) {
        try {
          await supabase.rpc('build_referral_chain', {
            p_new_user_id: newUser.id,
            p_referral_code: data.referralCode.trim().toUpperCase(),
          });
          // Generate one-time referral bonus for the direct referrer
          await supabase.rpc('generate_registration_referral_bonus', {
            p_new_user_id: newUser.id,
            p_referral_code: data.referralCode.trim().toUpperCase(),
          });
        } catch {
          // Non-critical: referral chain/bonus failure should not block registration
        }
      }

      toast.success('Account created! Redirecting to dashboard...');
      setTimeout(() => router.push('/home-dashboard'), 900);
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        toast.error('This email is already registered. Please sign in instead.');
      } else {
        toast.error(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div className="mb-2">
        <h2 className="text-xl font-bold text-foreground">Create your account</h2>
        <p className="text-sm text-muted-foreground mt-1">Start earning with GPU compute nodes</p>
      </div>

      {/* Full Name */}
      <div className="space-y-1.5">
        <label htmlFor="signup-name" className="block text-sm font-semibold text-foreground">
          Full Name
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <Icon name="UserIcon" size={18} />
          </div>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            className={`input-field pl-10 ${errors.fullName ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Your full name"
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' },
            })}
          />
        </div>
        {errors.fullName && (
          <p className="text-xs text-red-500 font-medium flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={13} />
            {errors.fullName.message}
          </p>
        )}
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="signup-email" className="block text-sm font-semibold text-foreground">
          Email Address
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <Icon name="EnvelopeIcon" size={18} />
          </div>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            className={`input-field pl-10 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
        </div>
        {errors.email && (
          <p className="text-xs text-red-500 font-medium flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={13} />
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <label htmlFor="signup-password" className="block text-sm font-semibold text-foreground">
          Password
        </label>
        <p className="text-xs text-muted-foreground">Minimum 8 characters with a number and symbol</p>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <Icon name="LockClosedIcon" size={18} />
          </div>
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            className={`input-field pl-10 pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Create a strong password"
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
              pattern: {
                value: /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
                message: 'Password must include a number and a symbol',
              },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-red-500 font-medium flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={13} />
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Confirm Password */}
      <div className="space-y-1.5">
        <label htmlFor="signup-confirm" className="block text-sm font-semibold text-foreground">
          Confirm Password
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <Icon name="LockClosedIcon" size={18} />
          </div>
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Repeat your password"
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => value === passwordValue || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="text-xs text-red-500 font-medium flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={13} />
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      {/* Referral Code */}
      <div className="space-y-1.5">
        <label htmlFor="signup-referral" className="block text-sm font-semibold text-foreground">
          Referral Code <span className="text-muted-foreground font-normal ml-1">(Optional)</span>
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <Icon name="GiftIcon" size={18} />
          </div>
          <input
            id="signup-referral"
            type="text"
            className="input-field pl-10 uppercase"
            placeholder="e.g. 0D942"
            {...register('referralCode', {
              pattern: { value: /^[A-Z0-9]{0,10}$/, message: 'Invalid referral code format' },
            })}
          />
        </div>
        {errors.referralCode && (
          <p className="text-xs text-red-500 font-medium flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={13} />
            {errors.referralCode.message}
          </p>
        )}
      </div>

      {/* Terms */}
      <div className="space-y-1.5">
        <div className="flex items-start gap-2.5">
          <input
            id="agree-terms"
            type="checkbox"
            className="w-4 h-4 rounded border-border text-primary focus:ring-ring cursor-pointer mt-0.5 flex-shrink-0"
            {...register('agreeTerms', { required: 'You must agree to the terms to continue' })}
          />
          <label htmlFor="agree-terms" className="text-sm text-muted-foreground cursor-pointer select-none leading-relaxed">
            I agree to the{' '}
            <span className="text-primary font-semibold hover:underline cursor-pointer">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary font-semibold hover:underline cursor-pointer">Privacy Policy</span>.
          </label>
        </div>
        {errors.agreeTerms && (
          <p className="text-xs text-red-500 font-medium flex items-center gap-1">
            <Icon name="ExclamationCircleIcon" size={13} />
            {errors.agreeTerms.message}
          </p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full py-3 text-base mt-2"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Creating account...
          </>
        ) : (
          <>
            <Icon name="UserPlusIcon" size={18} />
            Create Account
          </>
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary font-semibold hover:underline"
        >
          Sign in
        </button>
      </p>
    </form>
  );
}